import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { DsButton, Chip } from "../../ds";
import { Textarea } from "../ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import { STAGE_LABEL, dubai, OverdueBadge } from "./AfgViews";

const FIELDS = [
  ["Name", "name"], ["Company", "company"], ["Email", "email"], ["Phone", "phone"], ["Location", "location"],
  ["Product", "product"], ["Sector", "sector"], ["Water source", "water_source"],
  ["Capacity (m³/day)", "capacity_m3_day"], ["TDS (ppm)", "tds_ppm"], ["Need", "need"], ["Page", "page"],
  ["Quote ID", "quote_id"],
];

export const AfgLeadDrawer = ({ refId, includeTest, onClose, onChanged }) => {
  const { data, refetch } = useQuery({
    queryKey: ["afg-lead", refId, includeTest],
    queryFn: async () => (await api.get(`/afg/leads/${refId}`, { params: { include_test: includeTest } })).data,
    enabled: !!refId,
  });
  const lead = data?.lead;
  const [note, setNote] = useState("");
  useEffect(() => { setNote(lead?.ceo_note || ""); }, [lead?.ceo_note]);

  const patch = async (body, msg) => {
    try {
      await api.patch(`/afg/leads/${refId}`, body, { params: { include_test: includeTest } });
      toast.success(msg);
      refetch(); onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const terminal = ["won", "lost", "spam"].includes(lead?.ceo_stage);

  return (
    <Sheet open={!!refId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto rounded-none" data-testid="afg-lead-drawer">
        {lead && (
          <>
            <SheetHeader>
              <SheetTitle className="font-mono" data-testid="afg-drawer-ref">{lead.ref}</SheetTitle>
              <SheetDescription className="flex flex-wrap items-center gap-2">
                <Chip value={STAGE_LABEL[lead.stage]} testId="afg-drawer-stage" />
                <span>Received {dubai(lead.received_at)}</span>
                {lead.hours_to_quote != null && <span>· Quote after {lead.hours_to_quote} h</span>}
                <OverdueBadge lead={lead} />
              </SheetDescription>
            </SheetHeader>
            <div className="flex flex-wrap gap-2 my-4">
              {!terminal && ["won", "lost", "spam"].map((s) => (
                <DsButton key={s} variant={s === "won" ? "go" : "no"} className="h-8" onClick={() => patch({ stage: s }, `Marked ${s}.`)} data-testid={`afg-set-${s}`}>
                  Mark {s}
                </DsButton>
              ))}
              {terminal && <DsButton className="h-8" onClick={() => patch({ stage: "reopen" }, "Reopened.")} data-testid="afg-reopen">Reopen</DsButton>}
            </div>
            <dl className="grid grid-cols-[140px_1fr] gap-x-3 gap-y-1 text-sm">
              {FIELDS.filter(([, k]) => lead[k] != null && lead[k] !== "").map(([l, k]) => (
                <React.Fragment key={k}><dt className="text-[color:var(--muted)]">{l}</dt><dd data-testid={`afg-field-${k}`}>{String(lead[k])}</dd></React.Fragment>
              ))}
            </dl>
            <h4 className="mt-5 mb-1 font-mono text-[10.5px] uppercase tracking-[0.1em] text-[color:var(--muted)]">Message</h4>
            <p className="text-sm whitespace-pre-wrap" data-testid="afg-message">{lead.message || "—"}</p>
            <h4 className="mt-5 mb-1 font-mono text-[10.5px] uppercase tracking-[0.1em] text-[color:var(--muted)]">CEO note</h4>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="rounded-[4px]" data-testid="afg-note-input" />
            <DsButton className="h-8 mt-2" onClick={() => patch({ note }, "Note saved.")} data-testid="afg-note-save">Save note</DsButton>
            <h4 className="mt-5 mb-1 font-mono text-[10.5px] uppercase tracking-[0.1em] text-[color:var(--muted)]">Timeline</h4>
            <ol className="border-l border-[color:var(--line)] ml-1" data-testid="afg-timeline">
              {(data.events || []).map((e) => (
                <li key={e.event_id} className="pl-3 py-1.5 text-sm">
                  <span className="font-mono text-xs text-[color:var(--muted)]">{dubai(e.ts)} · {e.direction === "in" ? "In" : "Out"}</span>
                  <span className="block">{e.kind.replace(/_/g, " ")}{e.subject ? ` — ${e.subject}` : ""}</span>
                </li>
              ))}
            </ol>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
